/**
 * نسخةُ الجهاز — <b>ما يحتاجه الصندوقُ ليبيع وهو فاصل</b> (`0052`).
 *
 * ⚠ **`localStorage` لا قاعدةٌ في المتصفّح**: الكتالوجُ مئاتُ أصنافٍ (عشراتُ
 *   الكيلوبايتات)، والعمليّاتُ المنتظرةُ فواتيرُ يومٍ واحدٍ على الأكثر. والقراءةُ
 *   المتزامنةُ تُبقي الشاشاتِ بسيطة — ولا حاجةَ إلى ما هو أثقل.
 */

const PREFIX = 'pos:offline:'

export function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key)

    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

export function write(key: string, value: unknown) {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(PREFIX + key)
    } else {
      localStorage.setItem(PREFIX + key, JSON.stringify(value))
    }
  } catch {
    // مساحةٌ ممتلئة: النسخةُ تبقى قديمة، والبيعُ المتّصلُ لا يتأثّر.
  }

  window.dispatchEvent(new CustomEvent('pos-offline-store', { detail: key }))
}

/** يُنادى حين يتغيّر مفتاح — <b>ومن تبويبٍ آخر أيضًا</b>. */
export function subscribe(listener: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(PREFIX)) {
      listener()
    }
  }

  window.addEventListener('pos-offline-store', listener)
  window.addEventListener('storage', onStorage)

  return () => {
    window.removeEventListener('pos-offline-store', listener)
    window.removeEventListener('storage', onStorage)
  }
}

/** مُعرِّفُ هذا الجهاز — يُرسل مع كلّ عمليّةٍ ليُعرف من أين جاءت. */
export function deviceId(): string {
  let id = read<string | null>('device', null)

  if (!id) {
    id = crypto.randomUUID()
    write('device', id)
  }

  return id
}

// ── الأنواع ─────────────────────────────────────────────────────────────────

export type CatalogItem = {
  variantId: string
  sku: string
  productName: string
  label: string
  barcode: string | null
  barcodes: string[]
  price: number | null
  regularPrice: number | null
  minPrice: number | null
  currency: string
  onHand: number
}

export type Payee = { id: string; name: string }

/** المكان: فرعٌ ومستودع — <b>كما كانا آخرَ مرّةٍ متّصلًا</b>. */
export type Station = { branchId: string; warehouseId: string }

/**
 * الورديّةُ كما يعرفها الجهاز — <b>بمُعرِّف الخادم إن فُتحت متّصلًا</b>، أو
 * بمُعرِّف عمليّة فتحها إن فُتحت فاصلًا.
 */
export type LocalShift = {
  serverId: string | null
  clientId: string | null
  sessionNo: string
  userId: string
  openedAt: string
  openingCash: number
}

export type OpKind = 'SHIFT_OPEN' | 'SALE' | 'PAYOUT' | 'PUNCH' | 'SHIFT_CLOSE'

export type PendingOp = {
  clientId: string
  kind: OpKind
  occurredAt: string
  userId: string
  payload: Record<string, unknown>
  /** ما يُعرض للكاشير في قائمة «تنتظر»: «بيعة ٢٥٫٠٠٠ نقدًا». */
  summary: string
  /** مالٌ دخل الدرجَ (+) أو خرج منه (−) — لحساب ما في الدرج وهو فاصل. */
  cash: number
}

export type FailedOp = PendingOp & { errorCode: string | null; error: string | null }

export const keys = {
  catalog: (warehouseId: string) => `catalog:${warehouseId}`,
  catalogAt: (warehouseId: string) => `catalog-at:${warehouseId}`,
  payees: 'payees',
  employees: 'employees',
  station: 'station',
  shift: 'shift',
  queue: 'queue',
  failed: 'failed',
} as const
